import {
  KCMPPProfileConverted,
  KCMPPProfile,
  KCMPPFieldConverted,
  KCMPPField,
  KCMPPFeature,
  KCMPPLocalData,
} from "@/types/kcm-profile-api.types";
import { KcmCustomLocalMarket } from "@/types";
import store from "../state/store";

const deleteKeys: { [x in KCMPPFeature]: string[] } = {
  basic: ["affiliatecode", "paused_at", "pp_code", "remove_pp"],
  blog: [],
  graphics: [],
  guides: [],
  newsletter: [],
  video: ["autoadd", "hide_videos", "status"],
  local: [],
  realtalk: [],
};

const booleanFields: { [x in KCMPPFeature]: string[] } = {
  basic: [],
  blog: ["lead_capture", "prev_posted"],
  graphics: ["ehlogo", "eh_lenderlogo", "realtorlogo", "showlogo"],
  guides: ["ehlogo", "eh_lenderlogo", "front_customization"],
  newsletter: [
    "buyerad",
    "color_background",
    "ehlogo",
    "eh_lenderlogo",
    "realtorlogo",
    "sellerad",
    "show_header",
    "show_footer",
    "show_sidebar",
    "show_subtitle",
  ],
  video: ["custom_intro", "custom_outro"],
  local: [],
  realtalk: [],
};

const colorFields: { [x in KCMPPFeature]: string[] } = {
  basic: ["primary_color"],
  blog: [],
  graphics: ["bgcolor"],
  guides: [],
  newsletter: ["bgcolor"],
  video: [],
  local: [],
  realtalk: [],
};

const jsonFields: { [x in KCMPPFeature]: string[] } = {
  basic: [],
  blog: ["enabled_pages"],
  graphics: [],
  guides: [],
  newsletter: [],
  video: [],
  local: [],
  realtalk: ["realtalk_settings"],
};

const privacyPolicy: { [x: string]: string[] } = {
  en: [
    "The information you are providing to",
    "will be used solely for internal marketing purposes. We will not share any of your data with any other party (other than KCM, our hosting platform), without your prior written consent. By providing this information, you hereby consent to our contacting you via email/phone regarding your real estate need.",
  ],
  es: [
    "La información que usted está proporcionando a",
    "se utilizará únicamente con fines de mercadeo interno. No compartiremos ninguno de sus datos con nadie (que no sea KCM, nuestra plataforma de alojamiento), sin su consentimiento previo por escrito. Al proporcionar esta información, usted acepta que nos pongamos en contacto con usted por correo electrónico/teléfono con respecto a sus necesidades en bienes raíces.",
  ],
};

export const RE_PROF = "Real Estate Professional";
export const MORTGAGE_PROF = "Mortgage Professional";
export const ROLE_OTHER = "Other";

export const roleOptions = [
  {
    label: RE_PROF,
    value: RE_PROF,
    icon: "fa6-solid:house",
  },
  {
    label: MORTGAGE_PROF,
    value: MORTGAGE_PROF,
    icon: "fa6-solid:building-columns",
  },
  {
    label: ROLE_OTHER,
    value: ROLE_OTHER,
    icon: "fa6-solid:user-tie",
  },
];

export function translateFromAPI(feature: KCMPPFeature): KCMPPProfileConverted {
  const tempProfile = JSON.parse(
    JSON.stringify(store.getters["profile/getProfileByFeature"](feature))
  ) as KCMPPProfileConverted;

  /** 
    delete fields that we don't need in profile editor
    translate boolean fields
    append hashtag to color fields
    resolve json object fields
  **/
  Object.entries(tempProfile).forEach(([field]) => {
    if (deleteKeys[feature].includes(field)) {
      delete tempProfile[field];
    }

    if (booleanFields[feature].includes(field)) {
      // ensure the value is truthy and doesnt equal a string that signifies false
      tempProfile[field].value =
        tempProfile[field].value &&
        tempProfile[field].value !== "off" &&
        tempProfile[field].value !== "0"
          ? true
          : false;
    }

    if (colorFields[feature].includes(field)) {
      if (tempProfile[field].value) {
        tempProfile[field].value = "#" + tempProfile[field].value;
      } else {
        tempProfile[field].value =
          "#" + (process.env.VUE_APP_INDUSTRY_BRAND_COLOR ?? "00aeef");
      }
    }

    if (jsonFields[feature].includes(field)) {
      tempProfile[field].value = JSON.parse(tempProfile[field].value as string);
      Object.entries(tempProfile[field].value).forEach(([subfield, value]) => {
        (
          tempProfile[field].value as {
            [x: string]: boolean;
          }
        )[subfield] = value === 1 ? true : false;
      });
    }
  });

  if (feature === "basic") {
    if (!tempProfile.primary_role) {
      Object.assign(tempProfile, {
        primary_role: {
          SyncTo: "custom",
          fontSize: 0,
          value: "",
        },
      });
    }
  }

  if (feature === "blog") {
    if (tempProfile.privacy_policy) {
      tempProfile.privacy_policy.value = tempProfile.privacy_policy.value
        ? tempProfile.privacy_policy.value
        : getDefaultPrivacyPolicy("en");
    }
    if (tempProfile.privacy_policysp) {
      tempProfile.privacy_policysp.value = tempProfile.privacy_policysp.value
        ? tempProfile.privacy_policysp.value
        : getDefaultPrivacyPolicy("es");
    }
  }

  if (feature === "video") {
    if (!tempProfile.custom_intro) {
      Object.assign(tempProfile, {
        custom_intro: {
          SyncTo: "custom",
          fontSize: 0,
          value: false,
        },
      });
    }

    if (!tempProfile.custom_outro) {
      Object.assign(tempProfile, {
        custom_outro: {
          SyncTo: "custom",
          fontSize: 0,
          value: false,
        },
      });
    }
  }

  if (feature === "newsletter") {
    if (!tempProfile.show_header) {
      Object.assign(tempProfile, {
        show_header: {
          SyncTo: "custom",
          fontSize: 0,
          value: false,
        },
      });
    }
    if (!tempProfile.show_footer) {
      Object.assign(tempProfile, {
        show_footer: {
          SyncTo: "custom",
          fontSize: 0,
          value: true,
        },
      });
    }
    if (!tempProfile.show_sidebar) {
      Object.assign(tempProfile, {
        show_sidebar: {
          SyncTo: "custom",
          fontSize: 0,
          value: false,
        },
      });
    }
    if (!tempProfile.eh_lenderlogo) {
      Object.assign(tempProfile, {
        eh_lenderlogo: {
          SyncTo: "custom",
          fontSize: 0,
          value: false,
        },
      });
    }
    if (tempProfile.subtitletext) {
      Object.assign(tempProfile, {
        show_subtitle: {
          SyncTo: "custom",
          fontSize: 0,
          value: tempProfile.subtitletext.value !== "",
        },
      });
    }
    if (tempProfile.ppbackground) {
      Object.assign(tempProfile, {
        color_background: {
          SyncTo: "custom",
          fontSize: 0,
          value: tempProfile.ppbackground.value === "1",
        },
      });
    }
    if (!tempProfile.guides_location) {
      Object.assign(tempProfile, {
        guides_location: {
          SyncTo: "custom",
          fontSize: 0,
          value: 1,
        },
      });
    }
    if (!tempProfile.pplocation) {
      Object.assign(tempProfile, {
        pplocation: {
          SyncTo: "custom",
          fontSize: 0,
          value: 0,
        },
      });
    }
  }

  if (feature === "graphics") {
    if (!tempProfile.eh_lenderlogo) {
      Object.assign(tempProfile, {
        eh_lenderlogo: {
          SyncTo: "custom",
          fontSize: 0,
          value: false,
        },
      });
    }
  }

  return tempProfile;
}

export function translateToAPI(
  profile: KCMPPProfile | KCMPPProfileConverted,
  feature: KCMPPFeature
): KCMPPProfile {
  const tempProfile = JSON.parse(JSON.stringify(profile));

  /** 
    translate boolean fields
    remove hashtag from color fields
    resolve json object fields
  **/
  Object.entries(tempProfile).forEach(([field]) => {
    if (booleanFields[feature].includes(field)) {
      // hardcoding this, we shouldn't be using on/off for any other fields
      if (field === "lead_capture") {
        tempProfile[field].value = tempProfile[field].value ? "on" : "off";
      } else {
        // patch for saving default profiles until we can fix profile typing
        if (tempProfile[field].value === "0") {
          tempProfile[field].value = false;
        }
        tempProfile[field].value = tempProfile[field].value ? "1" : "0";
      }
    }

    if (colorFields[feature].includes(field)) {
      tempProfile[field].value = tempProfile[field].value
        .substring(1)
        .toUpperCase();
    }

    if (
      jsonFields[feature].includes(field) &&
      typeof tempProfile[field].value === "object"
    ) {
      Object.entries(tempProfile[field].value).forEach(([subfield, value]) => {
        tempProfile[field].value[subfield] = value ? 1 : 0;
      });

      tempProfile[field].value = JSON.stringify(tempProfile[field].value);
    }

    tempProfile[field].value = tempProfile[field].value.toString().trim();
  });

  // feature specific functionality should go here
  if (feature === "basic") {
    if (tempProfile.full_name)
      tempProfile.full_name.value = createFullName(tempProfile);
    if (tempProfile.location)
      tempProfile.location.value = createLocation(tempProfile);
  } else if (feature === "blog") {
    if (
      tempProfile.privacy_policy.value ===
      "<p>" + getDefaultPrivacyPolicy("en") + "</p>"
    ) {
      tempProfile.privacy_policy.value = "";
    }
    if (
      tempProfile.privacy_policysp.value ===
      "<p>" + getDefaultPrivacyPolicy("es") + "</p>"
    ) {
      tempProfile.privacy_policysp.value = "";
    }
  }

  return tempProfile;
}

export function getDefaultPrivacyPolicy(language: string): string {
  const policyTexts = privacyPolicy[language];
  return (
    policyTexts[0] +
    " " +
    store.getters["profile/profileFirstName"] +
    " " +
    policyTexts[1]
  );
}

export function updateTempProfileSyncTo(
  newValue: string,
  field: KCMPPField | KCMPPFieldConverted
): void {
  field.SyncTo = newValue.trim();
  if (field.SyncTo === "hide") {
    field.value = "";
  } else if (field.SyncTo === "custom") {
    field.value = "";
  } else {
    field.value =
      store.getters["profile/getProfileByFeature"]("basic")[field.SyncTo].value;
  }
}

export function updateTempProfileFontSize(
  newValue: string,
  field: KCMPPField | KCMPPFieldConverted
): void {
  field.fontSize = parseInt(newValue);
}

export function updateTempProfileValue(
  newValue: string,
  field: KCMPPField | KCMPPFieldConverted
): void {
  field.value = newValue.trim();
}

function createFullName(tempProfile: {
  first_name: KCMPPField;
  last_name: KCMPPField;
}): string {
  let first = "";
  let last = "";
  if (typeof tempProfile.first_name.value === "string")
    first = tempProfile.first_name.value.trim();
  if (typeof tempProfile.last_name.value === "string")
    last = tempProfile.last_name.value.trim();

  return (first + " " + last).trim();
}

function createLocation(tempProfile: {
  city: KCMPPField;
  state: KCMPPField;
}): string {
  let city = "";
  let state = "";
  if (typeof tempProfile.city.value === "string")
    city = tempProfile.city.value.trim();
  if (typeof tempProfile.state.value === "string")
    state = tempProfile.state.value.trim();

  return [city, state]
    .filter(function (v) {
      return v !== "";
    })
    .join(", ");
}

export function cloneProfileFields(
  profileFieldValue: KcmCustomLocalMarket[] | Record<string, string>[]
): KCMPPLocalData[] {
  const data: KCMPPLocalData[] = JSON.parse(JSON.stringify(profileFieldValue));
  return data;
}

export function allFieldsExist(
  profile: KCMPPProfile | KCMPPProfileConverted,
  fields: string[]
): boolean {
  const keys = Object.keys(profile);
  return !fields.some((field) => {
    return !keys.includes(field);
  });
}
